import React, { useContext, useRef, useCallback, useState, useEffect } from 'react';
import TextBlockWithQuillRenderer from '../TextBlockWithQuillRenderer';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { constructQuillJSON, constructAutoSaveJSON, convertContractToReact } from '../../../utils/TextBlockHelper';
import { dummyCallback } from '../../../utils/complexDocumentConstants';
import { useDefaultStyleContext } from '../../../context/complexDefaultStyleContext';

const TextElement  = (props) => {
  const containerRef = useRef(null);
  const fontChangeRef = useRef(null);
  const documentStyleRef = useRef(null);
  const updatingScaling = useRef(false);
  const [textEditable, setTextEditable] = useState(props.selected);
  const { element, selected, editable, scaling, pageId, onContainerClick = dummyCallback, isFromCreativeBlock = true, isStandalone=false, isHeaderEelement=false, isCreativeStaticBlock } = props;
  const { updateComplexElementDetails } = useContext(PageElementsContext);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);
  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    `TextElement-${element.uuid}`
  );

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled, updateArtboard] = useArtboardContext(previewModeSelector, `TextElement-${element.uuid}`);

  const currentDesignSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, '');

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexBlockRenderer-${props.itemUUID}`);

  useEffect(() => {
    const handleFontStyleUpdate = e => {
      if(isStandalone && currentDesign !=='block') return;
      const { action, newFontFamilyOrSize, styleType } = e.detail;
      const eventData =
        action === 'update'
          ? {
              fontStyleData: newFontFamilyOrSize,
              styleType,
              action,
            }
          : { action };
      if (containerRef?.current) {
        fontChangeRef.current = true;
        documentStyleRef.current = Object.assign({}, documentStyleRef.current,{[styleType]:newFontFamilyOrSize});
        setTextEditable(true);
        setTimeout(() => {
          containerRef.current.dispatchEvent(
            new CustomEvent('updateFontFamilySizeEvent', { detail: eventData }),
          );
        }, 500);
      }
    };
    const handleGridCellUpdate = e => {
      const { pasteCellUUID, action } = e.detail;
      if (containerRef?.current && pasteCellUUID === element.uuid) {
        setTextEditable(true);
        setTimeout(() => {
          containerRef.current.dispatchEvent(
            new CustomEvent('updateGridCellDataEvent', { detail: { action } }),
          );
        }, 500);
      }
    };
    const handleTextAlignment = e => {
      const { uuid } = e.detail;
      if(containerRef?.current && uuid === element.uuid) {
        fontChangeRef.current = true;
        setTextEditable(true);
        setTimeout(() => {
          containerRef.current.dispatchEvent(
            new CustomEvent('changeTextAlignment', { detail: e.detail }),
          );
        }, 500);
      }
    };

    const handleTextScaling = ({ detail }) => {
      const { action, item } = detail;
    
      updatingScaling.current = true;
    
      const hasAction = action !== undefined;
    
      const eventName = hasAction
        ? 'handleEditorUndoRedo'
        : 'updateContentsOnResizeEnd';
    
      const eventDetail = hasAction
        ? { action }
        : { scaling: item.scaling };
    
      const elementId = hasAction
        ? `toolbar-${item.uuid}`
        : `q-container-${item.uuid}`;
    
      const refElement = document.getElementById(elementId);
    
      if (refElement) {
        refElement.dispatchEvent(
          new CustomEvent(eventName, { detail: eventDetail })
        );
      }
    
      setTimeout(() => {
        updatingScaling.current = false;
      }, 1000);
    };
    
    const currentContainerRef = containerRef.current;
    documentStyleRef.current = documentStyle;
    document.addEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    document.addEventListener('gridCellUpdate', handleGridCellUpdate);
    document.addEventListener('alignText', handleTextAlignment);
    currentContainerRef.addEventListener('handleBulkTextScaling', handleTextScaling);
    return () => {
      document.removeEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
      document.removeEventListener('gridCellUpdate', handleGridCellUpdate);
      document.removeEventListener('alignText', handleTextAlignment);
      currentContainerRef.removeEventListener('handleBulkTextScaling', handleTextScaling);
    };
  }, []);

  useEffect(() => {
    if (editable) {
      documentStyleRef.current = documentStyle;
      setTextEditable(true);
    }
  }, [editable]);

  const saveBlock = useCallback((contents, undoDiff, redoDiff, cb, parentStyle = {}, op = undefined) => {
    if(updatingScaling.current) return
    if(fontChangeRef.current){
      fontChangeRef.current=false;
      cb();
      return;
    };
    const height = containerRef.current?.offsetHeight;
    updateComplexElementDetails(element.uuid, {
      contents: constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables),
      style: { ...(isFromCreativeBlock ? { height } : {}), ...parentStyle },
      undoDiff,
      redoDiff,
      op
    }, 'text-value-with-style');
    cb();
  }, [documentStyle, scaling]);

  const openSettingsInSidebar = useCallback((open = true) => {
    if (open) {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: element.uuid });
    } else {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    }
  }, [element.uuid]);

  const convertToQuill = useCallback((editable) => {
    if (editable) {
      const parentLayout = document.getElementById(`vertical-layout-${pageId}`);
      let currentScrollTop;
      currentScrollTop = parentLayout.scrollTop;
      setTextEditable(editable);
      return () => {
        parentLayout.scrollTop = currentScrollTop;
      };
    }
    return;
  }, [pageId]);

  return (
    <TextBlockWithQuillRenderer
      ref={containerRef}
      defaultContent={constructQuillJSON(element?.contents, documentStyleRef.current?.fontFamily||documentStyle?.fontFamily, documentStyleRef.current?.fontSize||documentStyle?.fontSize, globalVariables,pageOverflowEnabled)}
      saveBlock={saveBlock}
      isFromCreativeBlock={isFromCreativeBlock}
      editable={editable}
      itemUUID={pageOverflowEnabled ? `${element.uuid}-page-${pageId}` : element.uuid}
      selected={selected}
      enableSideBar={openSettingsInSidebar}
      scaling={scaling}
      isPreview={pageOverflowEnabled}
      adjustScroll={props.adjustScroll}
      onContainerClick={onContainerClick}
      textEditable={textEditable}
      setTextEditable={convertToQuill}
      isHeaderEelement={isHeaderEelement}
      enableFunctionCall={isInteractiveDesign ? false : true}
      isCreativeStaticBlock={isCreativeStaticBlock}
    >
      {pageOverflowEnabled || !textEditable ? convertContractToReact(element?.contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables) : null}
    </TextBlockWithQuillRenderer>
  )
}

export default TextElement;